import {NextPage} from "next";
import {useRouter} from "next/router";
import {useTranslation} from "@i18n/client";
import React, {useEffect} from "react";
import Notice from "src/components/v5/Maps/Notice";
import OtherProviders from "src/components/v5/OtherProviders";
import useLocationConstants, {isFullPageBanner} from "src/constants/LocationConstants";
import {isAlaskaAirlinesLoc, isPopupLoc, isSaasLocation} from "src/constants/locations";
import {RootStateApptReason, RootStateLocation, RootStatePracticeSpecialty} from "src/store/types";

import {ands} from "../../components/_common/Carbon";
import Layout from "../../components/v5/Layout";
import Main from "../../components/v5/Location/Main";
import Photos from "../../components/v5/Location/Photos";
import TestimonialSlider from "../../components/v5/TestimonialSlider";
import {NextReduxWrapper, useTypedSelector} from "../../store";
import {s3ImageSource} from "../../useS3ImgSrc";
import {getLocationSchemas} from "../../utils/schemaUtils";
import {useIsMobile} from "src/utils/isMobile";
import {
  setUserSelectedLocation,
  useUserSelectedLocation,
} from "src/utils/browser-storage/userSelectedLocation";
import {ignoreArrays} from "../../utils/ignoreArrays";
import {fetchLocalLocations} from "../../utils/fetchLocationsAndPractices";
import {forwardTo, v5Pages} from "../../components/_common/_constants";
import {SpecialtyId} from "../../constants/specialtyIds";
import {startBookingFlow} from "@page-containers/booking/utils/bookingQuery";
import {CareSlug} from "../../constants/cares";
import url from "url";
import {api} from "@services/api";
import {withLocaleServerSideProps} from "@i18n/pages";

type Metadata = {
  title?: string;
  description?: string;
  h1?: string;
};

type Props = {
  location: RootStateLocation;
  popularReasons: RootStateApptReason[] | [];
  metadata: Metadata | null;
};

const ClinicDetails: NextPage<Props> = ({location: _location, popularReasons, metadata}) => {
  const {query} = useRouter();
  const {slug} = query;

  const {regionSlug: selectedRegion} = useUserSelectedLocation();

  const isMobile = useIsMobile();

  const locations = useTypedSelector(state => state.config.locations);
  const location = _location || locations.find(l => l.slug === slug);

  const i18n = useTranslation();
  const isAlaska = isAlaskaAirlinesLoc(location.id);
  const {longitude, latitude} = location.address;

  const {images, name, specialties = {}} = location;

  const showImages = (images || []).length > 1;

  const isSaas = isSaasLocation(location);

  useEffect(() => {
    const selectedLocationsRegion = location.region.slug;
    if (selectedRegion !== selectedLocationsRegion) {
      setUserSelectedLocation({regionSlug: selectedLocationsRegion});
    }
  }, [location.region.slug, selectedRegion]);

  const pageTitle = specialties
    ? `${name || slug} | ${
        ands(
          i18n.t,
          specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
        ) || ""
      }`
    : "Carbon Health";

  const pageDescription = specialties
    ? `${ands(
        i18n.t,
        specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
      )} | ${name}`
    : "Carbon Health";

  const ogImage = s3ImageSource(((images || [])[0] || {}).imageId);

  // @ts-expect-error TS7006: Parameter 'd' implicitly has an 'any' type.
  const doctorFilter = d =>
    d.specialties.includes("Primary Care") && d.primaryLocationId === location.id;

  const bannerProps = useLocationConstants({locationId: location.id})?.banners.filter(b =>
    isFullPageBanner(b),
  )[0];

  const schemas = getLocationSchemas(location, ogImage);

  return (
    <Layout
      noIndex={isAlaska || isSaas}
      style={{backgroundColor: "#fff"}}
      pageTitle={metadata?.title || pageTitle}
      pageDescription={metadata?.description || pageDescription}
      className={!showImages ? "noImages" : ""}
      absolutePageImage={ogImage}
      navStartTransparent={false}
      schemas={schemas}
      blockChatWidget={isMobile}
      wrapClasses="ovf-unset"
      noHeader={isSaas}
      noFooter={isSaas}
    >
      {isSaas ? <></> : <div className="minh18 minh14-sm" />}
      {bannerProps && <Notice {...bannerProps} className="br0-f w100p-f jcc-f pt6-f pb4-f" />}
      {showImages && <Photos {...{location}} />}
      <Main
        h1={metadata?.h1}
        isPopup={isPopupLoc(location.name)}
        showImages={(images || []).length > 1}
        location={location}
        popularReasons={popularReasons}
      />
      <OtherProviders
        filter={doctorFilter}
        tag={location.name}
        header={i18n.t("Meet our {{clinic}} providers.", {clinic: location.name})}
        coordinate={{longitude, latitude}}
        radius={0.1}
      />
      {isSaas ? <></> : <TestimonialSlider />}
    </Layout>
  );
};

const getLandingPageMetadata = async (slug: string | string[]): Promise<Metadata> => {
  try {
    const response = await api.getWithTimeout(15000, `/hib/landing-pages/location/?slug=${slug}`);
    const json = await response.json();
    return json || {};
  } catch {
    return {};
  }
};

export const getServerSideProps = withLocaleServerSideProps(
  NextReduxWrapper.getServerSideProps<Props>(store => async ({query, res, resolvedUrl}) => {
    const slug = ignoreArrays(query.slug);
    const utmSource = ignoreArrays(query.utm_source);
    const reduxLocations = store?.getState().config.locations;

    const [metadata, locations] = await Promise.all([
      slug ? getLandingPageMetadata(slug) : Promise.resolve(null),
      reduxLocations ? Promise.resolve(reduxLocations) : fetchLocalLocations(),
    ]);

    const location = locations.find(l => l.slug === slug);

    if (!location) {
      return {
        redirect: {
          destination: v5Pages.locations,
          permanent: true,
        },
      };
    }

    const doBookingRedirect =
      utmSource &&
      ["gmb", "google"].includes(utmSource) &&
      location.specialties[SpecialtyId.URGENT_CARE];

    if (doBookingRedirect) {
      const [bookingLink] = startBookingFlow({
        appointmentReasonSlug: CareSlug.GENERAL_SICK_VISIT,
        locationSlug: slug,
        originUrl: resolvedUrl,
        specialtyId: SpecialtyId.URGENT_CARE,
      });
      await forwardTo(res, url.format(bookingLink), 307);
    }

    let popularReasons = [];

    if (location) {
      const url = `/hib/locations/${location.id}/popularApptReasons`;
      popularReasons = await api.get(url);
    }

    return {props: {location, popularReasons, metadata}};
  }),
);

export default ClinicDetails;
